/**
 * ActiveHoursInput — reusable sub-form for a campaign's active hours block
 * (Story 7.5). Emits the wire shape:
 *   { timezone, weekdays: number[], start: 'HH:MM', end: 'HH:MM' }
 *
 * Constraints (enforced server-side too — see `campaigns.schemas.ts`):
 *   - Timezone is a whitelist: `America/Sao_Paulo` + `UTC` for MVP.
 *     (Full tz picker lands with the scheduler story in E4.)
 *   - `weekdays` must be non-empty; 0=Domingo through 6=Sábado.
 *   - `start` < `end`; regex `^([01]\d|2[0-3]):[0-5]\d$`.
 *
 * The component is controlled: parent owns the value and receives `onChange`
 * on every mutation. Client-side validation is surfaced as inline error text
 * so the user does not discover the rules via an HTTP 400 round-trip.
 */

import { useEffect, useMemo, useRef } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type ActiveHoursTimezone = 'America/Sao_Paulo' | 'UTC';

export interface ActiveHoursValue {
  timezone: ActiveHoursTimezone;
  weekdays: number[];
  start: string;
  end: string;
}

export const ACTIVE_HOURS_TIMEZONES: ActiveHoursTimezone[] = ['America/Sao_Paulo', 'UTC'];

export const ACTIVE_HOURS_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const WEEKDAY_LABELS: Array<{ value: number; label: string; short: string }> = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda', short: 'Seg' },
  { value: 2, label: 'Terça', short: 'Ter' },
  { value: 3, label: 'Quarta', short: 'Qua' },
  { value: 4, label: 'Quinta', short: 'Qui' },
  { value: 5, label: 'Sexta', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
];

export const DEFAULT_ACTIVE_HOURS: ActiveHoursValue = {
  timezone: 'America/Sao_Paulo',
  weekdays: [1, 2, 3, 4, 5],
  start: '09:00',
  end: '20:00',
};

export interface ActiveHoursInputProps {
  value: ActiveHoursValue;
  onChange: (next: ActiveHoursValue) => void;
  disabled?: boolean;
  /**
   * Emitted when the local validation computes a new error text (or clears
   * it). The parent can use this to disable a submit button until the field
   * is coherent. Never called during render.
   */
  onValidityChange?: (error: string | null) => void;
}

export function ActiveHoursInput({
  value,
  onChange,
  disabled,
  onValidityChange,
}: ActiveHoursInputProps) {
  const error = useMemo(() => {
    if (!ACTIVE_HOURS_TIME_RE.test(value.start)) {
      return 'Início inválido — use o formato HH:MM.';
    }
    if (!ACTIVE_HOURS_TIME_RE.test(value.end)) {
      return 'Fim inválido — use o formato HH:MM.';
    }
    if (value.start >= value.end) {
      return 'Início deve ser anterior ao fim.';
    }
    if (value.weekdays.length === 0) {
      return 'Selecione ao menos um dia da semana.';
    }
    return null;
  }, [value]);

  // Report validity changes to the parent (post-render, not during render).
  // Using a tiny effect keeps the API stable without pulling in useReducer.
  // This is safe because `error` is already memoized.
  useReportValidity(error, onValidityChange);

  function toggleWeekday(day: number) {
    const has = value.weekdays.includes(day);
    const next = has
      ? value.weekdays.filter((w) => w !== day)
      : [...value.weekdays, day].sort((a, b) => a - b);
    onChange({ ...value, weekdays: next });
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="active-hours-timezone">Fuso horário</Label>
        <select
          id="active-hours-timezone"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value.timezone}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, timezone: e.target.value as ActiveHoursTimezone })}
        >
          {ACTIVE_HOURS_TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Dias da semana</legend>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Dias da semana">
          {WEEKDAY_LABELS.map((w) => {
            const selected = value.weekdays.includes(w.value);
            return (
              <button
                key={w.value}
                type="button"
                aria-pressed={selected}
                aria-label={w.label}
                disabled={disabled}
                onClick={() => toggleWeekday(w.value)}
                className={
                  'inline-flex h-8 min-w-[2.5rem] items-center justify-center rounded-md px-2 text-xs font-medium transition-colors ' +
                  (selected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent')
                }
              >
                {w.short}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="active-hours-start">Início</Label>
          <Input
            id="active-hours-start"
            type="time"
            value={value.start}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, start: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="active-hours-end">Fim</Label>
          <Input
            id="active-hours-end"
            type="time"
            value={value.end}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, end: e.target.value })}
          />
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Fuso MVP: {ACTIVE_HOURS_TIMEZONES.join(' + ')}. Outros fusos serão liberados em E4.
        </p>
      )}
    </div>
  );
}

/**
 * Tiny hook that pushes the validity string back to the parent without
 * forcing it to subscribe to every value change. Implemented inline to keep
 * the component dep graph simple.
 */
function useReportValidity(
  error: string | null,
  onValidityChange: ((error: string | null) => void) | undefined,
) {
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (!onValidityChange) return;
    if (last.current === error) return;
    last.current = error;
    onValidityChange(error);
  }, [error, onValidityChange]);
}

export default ActiveHoursInput;
